import {Area, Circle, Drawingmethod, Polygon} from "./mapmodels";

const DefaultPolygonClosePixelTolerance = 14;

export interface MapDrawingControllerCallbacks {
    onAreaComplete(area: Area): void;
}

export interface MapDrawingControllerOptions {
    polygonClosePixelTolerance?: number;
}

export class MapDrawingController {
    private readonly polygonClosePixelTolerance: number;
    private drawingmethod: Drawingmethod | null = Drawingmethod.POLYGON;
    private listeners: google.maps.MapsEventListener[] = [];
    private draftCircle: google.maps.Circle | null = null;
    private circleCenter: google.maps.LatLng | null = null;
    private draftPolyline: google.maps.Polyline | null = null;
    private polygonPoints: google.maps.LatLng[] = [];
    private previousDisableDoubleClickZoom: boolean | null = null;
    private previousDraggableCursor: string | null = null;

    constructor(
        private readonly map: google.maps.Map,
        private readonly callbacks: MapDrawingControllerCallbacks,
        options: MapDrawingControllerOptions = {},
    ) {
        this.polygonClosePixelTolerance = options.polygonClosePixelTolerance || DefaultPolygonClosePixelTolerance;
    }

    get isEnabled() {
        return this.listeners.length > 0;
    }

    setDrawingmethod(drawingmethod: Drawingmethod | null) {
        if(this.drawingmethod !== drawingmethod) {
            this.drawingmethod = drawingmethod;
            this.resetDraft();
        }
    }

    enable() {
        if(this.isEnabled) {
            return;
        }
        this.previousDisableDoubleClickZoom = !!this.map.get("disableDoubleClickZoom");
        const previousCursor = this.map.get("draggableCursor");
        this.previousDraggableCursor = typeof previousCursor === "string" ? previousCursor : null;
        this.map.set("disableDoubleClickZoom", true);
        this.map.set("draggableCursor", "crosshair");
        this.listeners = [
            window.google.maps.event.addListener(this.map, "click", (ev: google.maps.MouseEvent) => this.onDrawingClick(ev)),
            window.google.maps.event.addListener(this.map, "dblclick", (ev: google.maps.MouseEvent) => this.onDrawingDoubleClick(ev)),
            window.google.maps.event.addListener(this.map, "mousemove", (ev: google.maps.MouseEvent) => this.onDrawingMouseMove(ev)),
        ];
    }

    disable() {
        const wasEnabled = this.isEnabled;
        for(let listener of this.listeners) {
            listener.remove();
        }
        this.listeners = [];
        this.resetDraft();
        if(wasEnabled) {
            this.restoreMapOptions();
        }
    }

    destroy() {
        this.disable();
    }

    private onDrawingClick(ev: google.maps.MouseEvent) {
        ev.stop();
        if(this.drawingmethod === Drawingmethod.CIRCLE) {
            this.onCircleClick(ev.latLng);
        } else if(this.drawingmethod === Drawingmethod.POLYGON) {
            this.addPolygonPoint(ev.latLng);
        }
    }

    private onDrawingDoubleClick(ev: google.maps.MouseEvent) {
        ev.stop();
        if(this.drawingmethod !== Drawingmethod.POLYGON) {
            return;
        }
        if(this.polygonPoints.length === 0 || !this.sameLatLng(this.polygonPoints[this.polygonPoints.length - 1], ev.latLng)) {
            this.addPolygonPoint(ev.latLng);
        }
        this.finalizePolygon();
    }

    private onDrawingMouseMove(ev: google.maps.MouseEvent) {
        if(this.drawingmethod === Drawingmethod.CIRCLE && this.circleCenter != null && this.draftCircle != null) {
            this.draftCircle.setRadius(window.google.maps.geometry.spherical.computeDistanceBetween(this.circleCenter, ev.latLng));
        } else if(this.drawingmethod === Drawingmethod.POLYGON && this.draftPolyline != null && this.polygonPoints.length > 0) {
            this.draftPolyline.setPath(this.polygonPoints.concat(ev.latLng));
        }
    }

    private onCircleClick(latLng: google.maps.LatLng) {
        if(this.circleCenter == null) {
            this.circleCenter = latLng;
            this.draftCircle = new window.google.maps.Circle({
                center: latLng,
                radius: 0,
                clickable: false,
                map: this.map,
            });
            return;
        }
        if(this.draftCircle == null) {
            return;
        }
        this.draftCircle.setRadius(window.google.maps.geometry.spherical.computeDistanceBetween(this.circleCenter, latLng));
        const circle = this.draftCircle;
        this.draftCircle = null;
        this.circleCenter = null;
        this.callbacks.onAreaComplete(new Circle(circle));
    }

    private addPolygonPoint(latLng: google.maps.LatLng) {
        if(this.polygonPoints.length >= 3 && this.isClosingPolygonClick(latLng)) {
            this.finalizePolygon();
            return;
        }
        this.polygonPoints.push(latLng);
        if(this.draftPolyline == null) {
            this.draftPolyline = new window.google.maps.Polyline({
                path: this.polygonPoints,
                clickable: false,
                map: this.map,
            });
        } else {
            this.draftPolyline.setPath(this.polygonPoints);
        }
    }

    private finalizePolygon() {
        this.trimDuplicatePolygonPoints();
        if(this.polygonPoints.length < 3) {
            return;
        }
        if(this.draftPolyline != null) {
            this.draftPolyline.setMap(null);
            this.draftPolyline = null;
        }
        const polygon = new window.google.maps.Polygon({
            paths: this.polygonPoints.slice(),
            map: this.map,
        });
        this.polygonPoints = [];
        this.callbacks.onAreaComplete(new Polygon(polygon));
    }

    private isClosingPolygonClick(latLng: google.maps.LatLng) {
        if(this.polygonPoints.length === 0) {
            return false;
        }
        const projection = this.map.getProjection();
        if(projection == null) {
            return window.google.maps.geometry.spherical.computeDistanceBetween(this.polygonPoints[0], latLng) < 10;
        }
        const zoomScale = Math.pow(2, this.map.getZoom() || 0);
        const firstPoint = projection.fromLatLngToPoint(this.polygonPoints[0]);
        const clickedPoint = projection.fromLatLngToPoint(latLng);
        const x = (firstPoint.x - clickedPoint.x) * zoomScale;
        const y = (firstPoint.y - clickedPoint.y) * zoomScale;
        return Math.sqrt(x * x + y * y) <= this.polygonClosePixelTolerance;
    }

    private trimDuplicatePolygonPoints() {
        while(this.polygonPoints.length > 1 && this.sameLatLng(this.polygonPoints[this.polygonPoints.length - 1], this.polygonPoints[this.polygonPoints.length - 2])) {
            this.polygonPoints.pop();
        }
    }

    private sameLatLng(a: google.maps.LatLng, b: google.maps.LatLng) {
        return Math.abs(a.lat() - b.lat()) < 0.000000001 && Math.abs(a.lng() - b.lng()) < 0.000000001;
    }

    private resetDraft() {
        if(this.draftCircle != null) {
            this.draftCircle.setMap(null);
        }
        if(this.draftPolyline != null) {
            this.draftPolyline.setMap(null);
        }
        this.draftCircle = null;
        this.circleCenter = null;
        this.draftPolyline = null;
        this.polygonPoints = [];
    }

    private restoreMapOptions() {
        this.map.set("disableDoubleClickZoom", this.previousDisableDoubleClickZoom || false);
        this.map.set("draggableCursor", this.previousDraggableCursor);
        this.previousDisableDoubleClickZoom = null;
        this.previousDraggableCursor = null;
    }
}
